/**
 * The deep-link contract's ids, params, anchors and subjects: the actions the admin command palette
 * can deep-link to, the params each destination page reads back, and the record each action binds.
 * `admin-action-links.ts` re-exports all of it next to `adminActionLink`, which builds the hrefs;
 * import from there.
 */

import type { AdminEventFinancesTab } from "@/lib/routes/admin-dash-paths";

/** The actions the palette can deep-link to. More actions are added as their pages gain handlers. */
export const ADMIN_PAGE_ACTIONS = [
  "transfer-owner",
  "change-org",
  "set-status",
  "claim-event",
  "add-venues",
  "send-rfps",
  "open-venue",
  "mark-venue-unavailable",
  "mark-venue-booked",
  "invite-user",
  "grant-all-events",
  "add-events-to-user",
  "create-invoice",
  "edit-event-details",
  "open-landing-pages",
  "create-landing-page",
  "open-forms",
  "create-form",
  "new-note",
  "upload-event-documents",
  "cancel-event",
  "reopen-event",
  "edit-org-details",
  "create-group",
  "create-org-event",
  "new-organization",
  "add-user-to-group",
  "manage-event-access",
  "view-live-landing-page",
  "email-venue",
  "send-venue-rfp",
  "mark-venue-available",
  "unmark-venue-booked",
  "archive-venue",
  "unarchive-venue",
  "send-goodbyes",
  "open-proposals",
  "share-proposals",
  "negotiate-proposal",
  "request-contract",
  "decline-proposal",
  "set-priority",
  "assign-poc",
  "assign-va",
  "view-as-user",
  "open-my-client-dashboard",
  "view-quote",
  "enter-quote",
  "review-quotes",
  "create-event",
  "create-activity",
  "add-schedule-item",
  "create-email-template",
  "upload-contract",
  "open-ledger",
  "close-books",
  "add-charge",
  "upload-service-contract",
  "add-policy",
  "add-preferred-venue",
  "add-blacklisted-venue",
  "add-contract-clause",
  "add-contract-template",
  "duplicate-event",
  "show-cancelled-events",
  "show-org-groups",
  "rename-org",
  "change-org-address",
  "change-org-industry",
  "change-org-star-rating",
  "edit-org-deposit",
  "change-org-sourcing-fee",
  "reserve-subdomain",
  "upload-org-logo",
  "prioritize-office-venues",
  "import-preferred-venues",
  "add-preferred-chain",
  "add-preferred-vendor",
  "add-blacklisted-chain",
  "show-my-events",
  "show-unclaimed-events",
  "create-user",
  "show-my-orgs",
  "categorize-revenue-sources",
  "review-payment-links",
  "show-outstanding",
  "add-deal",
  "show-unlinked-deals",
  "show-events-needing-closing",
  "show-events-needing-commission",
  "show-events-needing-folio",
  "add-journal-entry",
  "create-subscription-contract",
  "show-last-month",
  "show-last-3-months",
  "show-attention-threads",
  "show-failing-threads",
  "show-action-threads",
  "show-wire-transfers",
  "show-card-expenses",
  "show-reconciliation-needs-closing",
  "show-commission-issues",
  "edit-user-profile",
  "rename-event",
  "change-venue-facing-name",
  "update-hotels",
  "open-agenda-planner",
  "resend-rfps",
  "search-notes",
  "email-partners",
  "show-quoted-venues",
  "show-quiet-venues",
  "fix-venue-photos",
  "show-bookmarked-proposals",
  "show-reviewed-contracts",
  "select-rfp-batch",
  "search-location",
  "show-bookmarked-search",
  "change-event-fee",
  "reopen-books",
  "open-credit-card",
  "create-payment-link",
  "enter-contract-terms",
  "enter-folio-amounts",
  "categorize-wires",
  "show-all-documents",
  "upload-signed-contract",
  "upload-final-folio",
  "generate-landing-page",
  "duplicate-landing-page",
  "create-hotel-booking-link",
  "generate-feedback-form",
  "duplicate-form",
  "open-activity-settings",
  "open-signup-limits",
  "open-calendar-sync",
  "open-schedule-settings",
  "open-comms-history",
  "open-text-templates",
  "open-comms-settings",
  "open-scheduled-sends",
  "show-proposals-table",
  "edit-venue-notes",
  "message-venue",
  "view-venue-emails",
  "manage-venue-photos",
  "new-venue-note",
  "view-proposal-quote",
  "undecline-proposal",
  "request-venue-proposal",
  "upload-venue-documents",
  "request-contract-approval",
  "comment-on-contract",
  "export-contract",
  "review-unmet-terms",
  "approve-contract",
  "rename-contract",
  "show-venues-missing-email",
  "add-venue-email",
  "import-venue-list",
  "show-bookmarked-venues",
  "show-archived-venues",
  "show-booked-venues",
  "show-declined-venues",
  "show-unavailable-venues",
  "show-not-contacted-venues",
  "show-rfp-sent-venues",
  "show-replied-venues",
  "negotiate-by-email",
  "rescrape-venue-images",
  "email-hotel-reps",
  "email-destination-partners",
  "open-venue-website",
  "read-venue-notes",
  "show-packages",
  "create-package",
  "compare-bookmarked-proposals",
  "show-awaiting-proposals",
  "show-declined-proposals",
  "show-unavailable-proposals",
  "ask-venue-about-requirements",
  "show-quoted-search",
  "open-search-filters",
  "set-travel-origin",
  "select-bookmarked-rfp-batch",
  "change-search-dates",
  "show-preferred-search",
] as const;

/** One deep-linkable admin action id. */
export type AdminPageAction = (typeof ADMIN_PAGE_ACTIONS)[number];

/**
 * The actions whose href is a plain link to an existing page or route, with no `action` param and
 * no landing: no page reads them back.
 */
export const ADMIN_LINK_ACTIONS = [
  "view-live-landing-page",
  "open-my-client-dashboard",
  "enter-quote",
  "review-quotes",
  "create-event",
  "create-org-event",
  "show-last-month",
  "show-last-3-months",
  "show-attention-threads",
  "show-failing-threads",
  "show-action-threads",
  "show-wire-transfers",
  "show-card-expenses",
  "show-reconciliation-needs-closing",
  "show-commission-issues",
  "show-proposals-table",
  "open-venue-website",
  "compare-bookmarked-proposals",
] as const satisfies readonly AdminPageAction[];

/** One link action id: a plain href, never read back by `useActionParam`. */
export type AdminLinkAction = (typeof ADMIN_LINK_ACTIONS)[number];

/** One landing action id: its href carries `action=<id>`, which the destination page reads back. */
export type AdminLandingAction = Exclude<AdminPageAction, AdminLinkAction>;

/**
 * Whether an action id is a landing action.
 *
 * @param action - Any action id.
 * @returns True unless it is a link action.
 */
function isLandingAction(action: AdminPageAction): action is AdminLandingAction {
  return !(ADMIN_LINK_ACTIONS as readonly string[]).includes(action);
}

/** Every landing action id, in contract order. */
export const ADMIN_LANDING_ACTIONS: readonly AdminLandingAction[] = ADMIN_PAGE_ACTIONS.filter(isLandingAction);

/** The query param a destination page reads to know which control to open. */
export const ACTION_PARAM = "action";

/**
 * The event details section id the ownership, set-status, claim-event, set-priority, assign-poc and
 * assign-va actions scroll to. Must equal `SECTION_IDS.OWNERSHIP` in
 * `src/features/event-details/_logic/constants.ts` (a test pins it).
 */
export const OWNERSHIP_ANCHOR = "ownership";

/**
 * The event details section id the manage-event-access action scrolls to. Must equal
 * `SECTION_IDS.USERS` in `src/features/event-details/_logic/constants.ts` (a test pins it).
 */
export const USERS_ANCHOR = "users";

/**
 * The event details section ids an `edit-event-details` link may carry as its hash. Must equal the
 * `SECTIONS` ids in `src/features/event-details/_logic/constants.ts` (a test pins it); kept here
 * because `lib/routes` does not import feature constants.
 */
export const EVENT_DETAILS_ANCHORS = [
  "logistics",
  "headcount",
  "event-dates",
  "rooms",
  "meeting-rooms",
  "agenda",
  "food-beverage",
  "budget-decision",
  "other",
  "users",
  "ownership",
] as const;

/** One event details section id an `edit-event-details` link may scroll to. */
export type EventDetailsAnchor = (typeof EVENT_DETAILS_ANCHORS)[number];

/**
 * The venue overlay's query param on the venues tab and the Proposals page (read by
 * `useVenueSelection` on both).
 */
export const VENUE_PARAM = "venue";

/**
 * The venue overlay's offer (`oee_quotes.quote_group_id`) on the venues tab and the Proposals page
 * (read by `useVenueSelection` on both). A `view-quote` landing opens the quote details on it.
 */
export const OFFER_PARAM = "offer";

/** The user a grant confirm opens for on an organization's Users page (read by `AdminOrgUsersPage`). */
export const USER_PARAM = "user";

/** The service contract whose detail opens on an event's Service contracts tab. */
export const CONTRACT_PARAM = "contract";

/** The event document (`invoice_files.id`) whose preview opens on an event's Documents tab. */
export const DOCUMENT_PARAM = "document";

/** The preferred venue (`enterprise_preferred_venues.id`) whose detail opens on the Preferred venues tab. */
export const PREFERRED_PARAM = "preferred";

/** The approval policy whose editor opens on an organization's Policies tab. */
export const POLICY_PARAM = "policy";

/** The organization a user detail page switches to (read by `useAddEventsLanding`). */
export const ORG_PARAM = "org";

/**
 * The group an add events modal opens for on a user detail page (read by `useAddEventsLanding`),
 * and the group whose Manage users opens on an organization's Users page.
 */
export const GROUP_PARAM = "group";

/** The record param the Charges & card tab reads to open one pending charge (`payments.id`). */
export const CHARGE_PARAM = "charge";

/** The record param the Folio reconciliation tab reads to open one booking's folio parser. */
export const BOOKING_PARAM = "booking";

/** The record param the Blacklisted venues settings tab reads to open one blacklisted venue. */
export const BLACKLISTED_PARAM = "blacklisted";

/** The record param the Contracts settings tab reads to open one contract template. */
export const TEMPLATE_PARAM = "template";

/**
 * The event details element id of the cancel and reopen zone, which resend-rfps scrolls to. Must
 * equal `EVENT_STATUS_SECTION_ID` in `src/features/event-cancellation/_logic/constants.ts`.
 */
export const EVENT_STATUS_ANCHOR = "event-status";

/**
 * Parses a deep-link id param strictly as a positive safe integer, so `"9x"`, `"1.5"`, `"0"` or an
 * empty value never resolve to some other record.
 *
 * @param raw - The raw param, or null when absent.
 * @returns The id, or null when absent or malformed.
 */
export function parsePositiveIntParam(raw: string | null): number | null {
  if (raw === null || raw === "") return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** An action on one event. */
export interface EventActionSubject {
  eventId: number;
}

/** The event details editor, optionally opened at one section. */
export interface EventDetailsActionSubject extends EventActionSubject {
  /** The section to scroll to, carried as the hash; omitted lands on Logistics. */
  section?: EventDetailsAnchor;
}

/** An action that opens a control shared by every Finances tab, on the tab given (Overview when omitted). */
export interface EventFinancesTabActionSubject extends EventActionSubject {
  tab?: AdminEventFinancesTab;
}

/** An action on one venue, shown on one event's venues tab. */
export interface VenueActionSubject {
  venueId: number;
  eventId: number;
}

/** The quote details for one venue on one event, optionally at one offer. */
export interface VenueQuoteActionSubject extends VenueActionSubject {
  /** The offer to open (`oee_quotes.quote_group_id`); omitted opens the venue's headline offer. */
  quoteGroupId?: string;
}

/** A venue overlay action whose control lives in the overlay on both the venues tab and Proposals. */
export interface VenueOverlayTabActionSubject extends VenueActionSubject {
  /** The Source tab whose overlay the action opens in; omitted opens it on the venues tab. */
  tab?: "venues" | "proposals";
}

/** The Compare screen for some of an event's proposals. */
export interface ProposalCompareActionSubject extends EventActionSubject {
  /**
   * The venues to compare (`venue_static_id`s), in column order. Omitted or empty links to the
   * Proposals grid instead, since the Compare screen needs at least one venue.
   */
  venueIds?: readonly number[];
}

/** An action on one uploaded source contract, on its detail page. */
export interface ContractActionSubject extends EventActionSubject {
  contractId: number;
}

/** An action on one organization. */
export interface OrgActionSubject {
  orgId: number;
}

/** An action on one user, shown on one organization's Users page. */
export interface UserActionSubject {
  userId: number;
  orgId: number;
}

/** An action on one user in one organization, optionally for one of their groups there. */
export interface UserGroupActionSubject extends UserActionSubject {
  /** The group to open the action for; omitted lets the landing page pick the user's only group. */
  groupId?: number;
}

/** An action on one user, run in one organization when one is given. */
export interface UserOptionalOrgActionSubject {
  userId: number;
  /** The organization the landing opens on; omitted lets the landing page ask. */
  orgId?: number;
}

/** An action that needs no record, such as creating a new organization. */
export type GlobalActionSubject = Record<never, never>;

/** The subject each action needs to build its href. */
export interface AdminActionSubjects {
  "transfer-owner": EventActionSubject;
  "change-org": EventActionSubject;
  "set-status": EventActionSubject;
  "claim-event": EventActionSubject;
  "add-venues": EventActionSubject;
  "send-rfps": EventActionSubject;
  "open-venue": VenueActionSubject;
  "mark-venue-unavailable": VenueActionSubject;
  "mark-venue-booked": VenueActionSubject;
  "invite-user": OrgActionSubject;
  "grant-all-events": UserActionSubject;
  "add-events-to-user": UserGroupActionSubject;
  "create-invoice": EventActionSubject;
  "edit-event-details": EventDetailsActionSubject;
  "open-landing-pages": EventActionSubject;
  "create-landing-page": EventActionSubject;
  "open-forms": EventActionSubject;
  "create-form": EventActionSubject;
  "new-note": EventActionSubject;
  "upload-event-documents": EventFinancesTabActionSubject;
  "cancel-event": EventActionSubject;
  "reopen-event": EventActionSubject;
  "edit-org-details": OrgActionSubject;
  "create-group": OrgActionSubject;
  "create-org-event": OrgActionSubject;
  "new-organization": GlobalActionSubject;
  "add-user-to-group": UserOptionalOrgActionSubject;
  "manage-event-access": EventActionSubject;
  "view-live-landing-page": EventActionSubject;
  "email-venue": VenueActionSubject;
  "send-venue-rfp": VenueActionSubject;
  "mark-venue-available": VenueActionSubject;
  "unmark-venue-booked": VenueActionSubject;
  "archive-venue": VenueActionSubject;
  "unarchive-venue": VenueActionSubject;
  "send-goodbyes": EventActionSubject;
  "open-proposals": EventActionSubject;
  "share-proposals": EventActionSubject;
  "negotiate-proposal": VenueActionSubject;
  "request-contract": VenueActionSubject;
  "decline-proposal": VenueActionSubject;
  "set-priority": EventActionSubject;
  "assign-poc": EventActionSubject;
  "assign-va": EventActionSubject;
  "view-as-user": UserOptionalOrgActionSubject;
  "open-my-client-dashboard": GlobalActionSubject;
  "view-quote": VenueQuoteActionSubject;
  "enter-quote": VenueActionSubject;
  "review-quotes": GlobalActionSubject;
  "create-event": GlobalActionSubject;
  "create-activity": EventActionSubject;
  "add-schedule-item": EventActionSubject;
  "create-email-template": EventActionSubject;
  "upload-contract": EventActionSubject;
  "open-ledger": EventFinancesTabActionSubject;
  "close-books": EventActionSubject;
  "add-charge": EventActionSubject;
  "upload-service-contract": EventActionSubject;
  "add-policy": OrgActionSubject;
  "add-preferred-venue": OrgActionSubject;
  "add-blacklisted-venue": OrgActionSubject;
  "add-contract-clause": OrgActionSubject;
  "add-contract-template": OrgActionSubject;
  "duplicate-event": OrgActionSubject;
  "show-cancelled-events": OrgActionSubject;
  "show-org-groups": OrgActionSubject;
  "rename-org": OrgActionSubject;
  "change-org-address": OrgActionSubject;
  "change-org-industry": OrgActionSubject;
  "change-org-star-rating": OrgActionSubject;
  "edit-org-deposit": OrgActionSubject;
  "change-org-sourcing-fee": OrgActionSubject;
  "reserve-subdomain": OrgActionSubject;
  "upload-org-logo": OrgActionSubject;
  "prioritize-office-venues": OrgActionSubject;
  "import-preferred-venues": OrgActionSubject;
  "add-preferred-chain": OrgActionSubject;
  "add-preferred-vendor": OrgActionSubject;
  "add-blacklisted-chain": OrgActionSubject;
  "show-my-events": GlobalActionSubject;
  "show-unclaimed-events": GlobalActionSubject;
  "create-user": GlobalActionSubject;
  "show-my-orgs": GlobalActionSubject;
  "categorize-revenue-sources": GlobalActionSubject;
  "review-payment-links": GlobalActionSubject;
  "show-outstanding": GlobalActionSubject;
  "add-deal": GlobalActionSubject;
  "show-unlinked-deals": GlobalActionSubject;
  "show-events-needing-closing": GlobalActionSubject;
  "show-events-needing-commission": GlobalActionSubject;
  "show-events-needing-folio": GlobalActionSubject;
  "add-journal-entry": GlobalActionSubject;
  "create-subscription-contract": GlobalActionSubject;
  "show-last-month": GlobalActionSubject;
  "show-last-3-months": GlobalActionSubject;
  "show-attention-threads": GlobalActionSubject;
  "show-failing-threads": GlobalActionSubject;
  "show-action-threads": GlobalActionSubject;
  "show-wire-transfers": GlobalActionSubject;
  "show-card-expenses": GlobalActionSubject;
  "show-reconciliation-needs-closing": GlobalActionSubject;
  "show-commission-issues": GlobalActionSubject;
  "edit-user-profile": UserOptionalOrgActionSubject;
  "rename-event": EventActionSubject;
  "change-venue-facing-name": EventActionSubject;
  "update-hotels": EventActionSubject;
  "open-agenda-planner": EventActionSubject;
  "resend-rfps": EventActionSubject;
  "search-notes": EventActionSubject;
  "email-partners": EventActionSubject;
  "show-quoted-venues": EventActionSubject;
  "show-quiet-venues": EventActionSubject;
  "fix-venue-photos": EventActionSubject;
  "show-bookmarked-proposals": EventActionSubject;
  "show-reviewed-contracts": EventActionSubject;
  "select-rfp-batch": EventActionSubject;
  "search-location": EventActionSubject;
  "show-bookmarked-search": EventActionSubject;
  "change-event-fee": EventActionSubject;
  "reopen-books": EventActionSubject;
  "open-credit-card": EventActionSubject;
  "create-payment-link": EventActionSubject;
  "enter-contract-terms": EventActionSubject;
  "enter-folio-amounts": EventActionSubject;
  "categorize-wires": EventActionSubject;
  "show-all-documents": EventActionSubject;
  "upload-signed-contract": EventFinancesTabActionSubject;
  "upload-final-folio": EventFinancesTabActionSubject;
  "generate-landing-page": EventActionSubject;
  "duplicate-landing-page": EventActionSubject;
  "create-hotel-booking-link": EventActionSubject;
  "generate-feedback-form": EventActionSubject;
  "duplicate-form": EventActionSubject;
  "open-activity-settings": EventActionSubject;
  "open-signup-limits": EventActionSubject;
  "open-calendar-sync": EventActionSubject;
  "open-schedule-settings": EventActionSubject;
  "open-comms-history": EventActionSubject;
  "open-text-templates": EventActionSubject;
  "open-comms-settings": EventActionSubject;
  "open-scheduled-sends": EventActionSubject;
  "show-proposals-table": EventActionSubject;
  "edit-venue-notes": VenueActionSubject;
  "message-venue": VenueOverlayTabActionSubject;
  "view-venue-emails": VenueOverlayTabActionSubject;
  "manage-venue-photos": VenueActionSubject;
  "new-venue-note": VenueActionSubject;
  "view-proposal-quote": VenueQuoteActionSubject;
  "undecline-proposal": VenueActionSubject;
  "request-venue-proposal": VenueActionSubject;
  "upload-venue-documents": VenueActionSubject;
  "request-contract-approval": ContractActionSubject;
  "comment-on-contract": ContractActionSubject;
  "export-contract": ContractActionSubject;
  "review-unmet-terms": ContractActionSubject;
  "approve-contract": ContractActionSubject;
  "rename-contract": ContractActionSubject;
  "show-venues-missing-email": EventActionSubject;
  "add-venue-email": VenueActionSubject;
  "import-venue-list": EventActionSubject;
  "show-bookmarked-venues": EventActionSubject;
  "show-archived-venues": EventActionSubject;
  "show-booked-venues": EventActionSubject;
  "show-declined-venues": EventActionSubject;
  "show-unavailable-venues": EventActionSubject;
  "show-not-contacted-venues": EventActionSubject;
  "show-rfp-sent-venues": EventActionSubject;
  "show-replied-venues": EventActionSubject;
  "negotiate-by-email": VenueActionSubject;
  "rescrape-venue-images": VenueActionSubject;
  "email-hotel-reps": EventActionSubject;
  "email-destination-partners": EventActionSubject;
  "open-venue-website": VenueActionSubject;
  "read-venue-notes": VenueActionSubject;
  "show-packages": EventActionSubject;
  "create-package": EventActionSubject;
  "compare-bookmarked-proposals": ProposalCompareActionSubject;
  "show-awaiting-proposals": EventActionSubject;
  "show-declined-proposals": EventActionSubject;
  "show-unavailable-proposals": EventActionSubject;
  "ask-venue-about-requirements": VenueActionSubject;
  "show-quoted-search": EventActionSubject;
  "open-search-filters": EventActionSubject;
  "set-travel-origin": EventActionSubject;
  "select-bookmarked-rfp-batch": EventActionSubject;
  "change-search-dates": EventActionSubject;
  "show-preferred-search": EventActionSubject;
}

/**
 * Whether a value is one of the deep-linkable action ids.
 *
 * @param value - A raw `action` param or other string.
 * @returns True for a known action id.
 */
export function isAdminPageAction(value: string | null | undefined): value is AdminPageAction {
  return typeof value === "string" && (ADMIN_PAGE_ACTIONS as readonly string[]).includes(value);
}
